import { IfAbility } from '@efdigital/ll-addon-auth';
import { DataTypeXceed } from '@efdigital/ll-addon-data/data-types/xceed';
import type { Cohort } from '@efdigital/ll-addon-data/types/xceed/cohort';
import type { Program } from '@efdigital/ll-addon-data/types/xceed/program';
import { Heading } from '@efdigital/ll-addon-ui';
import { EditContext } from '@efdigital/ll-admin-v2/components/edit-context.gts';
import { CohortListing } from '@efdigital/ll-admin-v2/components/programs/cohort-listing.gts';
import { EditCohort } from '@efdigital/ll-admin-v2/components/programs/edit-cohort.gts';
import { EditProgram } from '@efdigital/ll-admin-v2/components/programs/edit-program';
import { jsonApiOptions } from '@efdigital/ll-admin-v2/lib/json-api.ts';
import { generateRandomSlug } from '@efdigital/ll-admin-v2/lib/random-slug';
import type { ModelFrom } from '@efdigital/ll-admin-v2/lib/type-utils';
import type ProgramsProgramRoute from '@efdigital/ll-admin-v2/routes/programs/program';
import { array, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';
import type { FindRecordOptions } from '@warp-drive/core/types/request';
import { Request } from '@warp-drive/ember';
import { findRecord, query } from '@warp-drive/utilities/json-api';
import BsModal from 'ember-bootstrap/components/bs-modal';
import { pageTitle } from 'ember-page-title';

interface IndexSignature {
  Args: {
    model: ModelFrom<ProgramsProgramRoute>;
    controller: unknown;
  };
}

export default class ProgramsProgramIndexTemplate extends Component<IndexSignature> {
  @service declare store: Store;
  @tracked modalOpen = false;
  @tracked newCohort?: Cohort;

  get findProgram() {
    return findRecord<Program>(
      DataTypeXceed.Program,
      this.args.model.program_id,
      {
        ...jsonApiOptions,
        include: ['client'],
      } as FindRecordOptions
    );
  }

  get findCohorts() {
    return query<Cohort>(
      DataTypeXceed.Cohort,
      {
        'filter[program.id]': this.args.model.program_id,
        'include[]': ['program', 'group', 'admingroup'],
      },
      jsonApiOptions
    );
  }

  showCreateModal = async (program: Program) => {
    const client = await program.client;
    if (!client) {
      window.alert(
        'Please assign a client to the program before creating a cohort.'
      );
      return;
    }

    this.newCohort = this.store.createRecord<Cohort>(DataTypeXceed.Cohort, {
      program,
      slug: generateRandomSlug(),
      client,
      systemLineage: 'll-admin-v2',
    });
    this.modalOpen = true;
  };

  hideCreateModal = (reload: () => void) => {
    this.modalOpen = false;
    this.newCohort = undefined;
    reload();
  };

  <template>
    <div class="container py-5">
      {{pageTitle "Program details"}}

      <Request @query={{this.findProgram}}>
        <:content as |content|>
          <Heading @text={{content.data.title}} />

          <IfAbility @ability="programs" @atLeast="editor">
            <:if>
              <EditContext as |context|>
                <EditProgram
                  @item={{content.data}}
                  @context={{context}}
                  class="col-md-8 mx-auto"
                />
              </EditContext>
            </:if>
          </IfAbility>

          <hr class="my-5" />

          <Request @query={{this.findCohorts}}>
            <:content as |cohorts state|>
              <Heading
                @text="Cohorts"
                @secondary="Manage cohorts for this program"
              />

              <IfAbility @ability="cohorts" @atLeast="editor">
                <:if>
                  <div class="d-flex justify-content-center mb-5">
                    <button
                      type="button"
                      class="btn btn-primary btn-md"
                      {{on "click" (fn this.showCreateModal content.data)}}
                    >New Cohort</button>
                  </div>
                </:if>
              </IfAbility>

              {{#if cohorts.data.length}}
                <div class="row justify-content-center">
                  {{#each cohorts.data as |cohort|}}
                    <div class="col-md-6 col-lg-4 mb-3">
                      <CohortListing @cohort={{cohort}} class="h-100">
                        <LinkTo
                          @route="programs.program.cohort"
                          @models={{array @model.program_id cohort.id}}
                          class="btn btn-primary btn-sm stretched-link"
                        >View Cohort</LinkTo>
                      </CohortListing>
                    </div>
                  {{/each}}
                </div>
              {{/if}}

              <BsModal
                @open={{this.modalOpen}}
                @onHidden={{fn this.hideCreateModal state.reload}}
                @backdropClose={{false}}
                @size="xl"
                as |modal|
              >
                <modal.header>
                  <h3 class="modal-title h4">Create Cohort</h3>
                </modal.header>
                <modal.body>
                  {{#if this.newCohort}}
                    <div class="row">
                      <div class="col-md-8">
                        <EditContext
                          @onEditingStopped={{fn
                            this.hideCreateModal
                            state.reload
                          }}
                          as |context|
                        >
                          <EditCohort
                            @item={{this.newCohort}}
                            @context={{context}}
                          />
                        </EditContext>
                      </div>
                      <div class="col-md-4">
                        <h4 class="gud-eyebrow">Preview</h4>
                        {{#if this.newCohort.title}}
                          <CohortListing @cohort={{this.newCohort}} />
                        {{else}}
                          <div
                            class="py-3 px-5 text-center img-thumbnail"
                          >Please enter a title to start creating your cohort.</div>
                        {{/if}}
                      </div>
                    </div>
                  {{/if}}
                </modal.body>
              </BsModal>
            </:content>
          </Request>
        </:content>
      </Request>
    </div>
  </template>
}
